<div class="flex flex-col gap-10">
  <!-- Header -->
  <div class="header">
    <div class="flex items-center gap-3">
      <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <mat-icon class="text-2xl">handshake</mat-icon>
      </div>
      <div>
        <h2 class="text-2xl font-semibold text-gray-12">Mandat & Attentes</h2>
        <p class="mt-1 text-sm text-gray-11">
          Définissez vos règles de mandat, votre taux cible et vos priorités pour guider les agents candidats.
        </p>
      </div>
    </div>
  </div>

  <!-- Educational Info Box -->
  <div class="rounded-2xl border border-blue-a4 bg-blue-a2 p-5">
    <div class="flex items-start gap-4">
      <mat-icon class="mt-0.5 text-blue-11">lightbulb</mat-icon>
      <div class="flex-1 text-sm">
        <h4 class="font-semibold text-gray-12">Redonnez du pouvoir à votre projet de vente</h4>
        <p class="mt-1 text-gray-11 leading-relaxed">
          Sur ETXE, vous ne subissez plus le démarchage. C'est vous qui fixez le cadre. Les agents immobiliers
          certifiés consulteront votre dossier et vous proposeront leur meilleure stratégie (visuels
          professionnels, sélection bancaire des acheteurs, accompagnement sur-mesure) en s'alignant sur vos
          critères.
        </p>
      </div>
    </div>
  </div>

  <div class="flex flex-col gap-10">
    <!-- SECTION 1: Type de mandat souhaité -->
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-base font-semibold text-gray-12">1. Type de mandat souhaité</h3>
        <p class="mt-0.5 text-xs text-gray-11">
          Choisissez la formule contractuelle avec laquelle vous souhaitez travailler avec votre futur agent.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        @for (opt of mandateTypes; track opt.value) {
          @let isSelected = model().type === opt.value;
          <button
            type="button"
            (click)="selectMandateType(opt.value)"
            class="group relative flex cursor-pointer flex-col justify-between rounded-2xl border p-5 text-left transition-all duration-200"
            [class.border-primary]="isSelected"
            [class.bg-primary-a1]="isSelected"
            [class.ring-2]="isSelected"
            [class.ring-primary/20]="isSelected"
            [class.border-gray-6]="!isSelected"
            [class.hover:border-gray-8]="!isSelected"
            [class.bg-gray-1]="!isSelected"
          >
            <div>
              <div class="flex items-start justify-between gap-2">
                <div class="flex items-center gap-2">
                  <div
                    class="flex h-5 w-5 items-center justify-center rounded-full border transition-colors"
                    [class.border-primary]="isSelected"
                    [class.bg-primary]="isSelected"
                    [class.border-gray-7]="!isSelected"
                  >
                    @if (isSelected) {
                      <div class="h-2 w-2 rounded-full bg-white"></div>
                    }
                  </div>
                  <h4 class="font-semibold text-gray-12 text-sm">{{ opt.title }}</h4>
                </div>

                @if (opt.badge) {
                  <span
                    class="rounded-full border px-2.5 py-0.5 text-[11px] font-medium {{
                      opt.badgeColorClass
                    }}"
                  >
                    {{ opt.badge }}
                  </span>
                }
              </div>

              <p class="mt-3 text-xs text-gray-11 leading-relaxed">
                {{ opt.description }}
              </p>
            </div>

            <div class="mt-4 border-t border-gray-5 pt-3">
              <ul class="space-y-1.5 text-[11px] text-gray-11">
                @for (benefit of opt.benefits; track benefit) {
                  <li class="flex items-center gap-1.5">
                    <mat-icon class="text-[14px] w-3.5 h-3.5 text-primary">check_circle</mat-icon>
                    <span>{{ benefit }}</span>
                  </li>
                }
                @if (opt.drawbacks?.length) {
                  @for (drawback of opt.drawbacks; track drawback) {
                    <li class="flex items-center gap-1.5 text-amber-11 font-medium">
                      <mat-icon class="text-[14px] w-3.5 h-3.5 text-amber-10">remove_circle_outline</mat-icon>
                      <span>{{ drawback }}</span>
                    </li>
                  }
                }
              </ul>
            </div>
          </button>
        }
      </div>
    </div>

    <!-- SECTION 2: Durée d'engagement souhaitée -->
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-base font-semibold text-gray-12">2. Durée d'engagement souhaitée</h3>
        <p class="mt-0.5 text-xs text-gray-11">
          Durée initiale du mandat pendant laquelle l'agent dispose de la commercialisation.
        </p>
      </div>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        @for (item of durations; track item.value) {
          @let isSelected = model().duration === item.value;
          <button
            type="button"
            (click)="selectDuration(item.value)"
            class="flex flex-col items-center justify-center rounded-xl border p-4 text-center transition-all duration-200 cursor-pointer"
            [class.border-primary]="isSelected"
            [class.bg-primary-a1]="isSelected"
            [class.ring-1]="isSelected"
            [class.ring-primary]="isSelected"
            [class.border-gray-6]="!isSelected"
            [class.bg-gray-1]="!isSelected"
            [class.hover:border-gray-8]="!isSelected"
          >
            <span
              class="text-sm font-semibold"
              [class.text-primary]="isSelected"
              [class.text-gray-12]="!isSelected"
            >
              {{ item.label }}
            </span>
            <span class="mt-1 text-[11px] text-gray-11">{{ item.hint }}</span>
          </button>
        }
      </div>
    </div>

    <!-- SECTION 3: Taux d'honoraires max / cible -->
    <div class="flex flex-col gap-5 rounded-2xl border border-gray-6 bg-gray-1 p-6">
      <div class="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div>
          <div class="flex items-center gap-2">
            <h3 class="text-base font-semibold text-gray-12">3. Taux d'honoraires maximum ciblé</h3>
            @if (model().type) {
              <span
                class="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary border border-primary/20"
              >
                Fourchette
                {{
                  model().type === PropertyMandateType.EXCLUSIVE
                    ? 'Mandat Exclusif'
                    : model().type === PropertyMandateType.SIMPLE
                      ? 'Mandat Simple'
                      : 'Mandat Semi-Exclusif'
                }}
              </span>
            } @else {
              <span
                class="rounded-full bg-gray-a3 px-2.5 py-0.5 text-[11px] font-medium text-gray-11 border border-gray-a5"
              >
                Fourchette indicative
              </span>
            }
          </div>
          <p class="mt-1 text-xs text-gray-11 leading-relaxed">
            {{ commissionConfig().adviceHint }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <div
            class="flex items-center rounded-xl border border-gray-6 bg-surface-default px-3 py-1.5 shadow-sm"
          >
            <span class="text-lg font-bold text-primary">
              {{
                model().targetCommissionRate
                  ? (model().targetCommissionRate | number: '1.1-2') + ' %'
                  : 'À discuter'
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Slider & Inputs -->
      <div class="flex flex-col gap-4">
        <div class="px-2">
          <mat-slider
            [min]="commissionConfig().min"
            [max]="commissionConfig().max"
            step="0.1"
            discrete
            class="w-full"
          >
            <input
              matSliderThumb
              aria-label="Taux de commission cible"
              [ngModel]="model().targetCommissionRate || commissionConfig().defaultRate"
              (ngModelChange)="updateCommissionRate($event)"
            />
          </mat-slider>
          <div class="flex justify-between text-[11px] font-medium text-gray-10">
            <span>{{ commissionConfig().minLabel }}</span>
            <span class="text-primary font-semibold">{{ commissionConfig().recommendedRangeText }}</span>
            <span>{{ commissionConfig().maxLabel }}</span>
          </div>
        </div>

        <!-- Dynamic Calculation Card -->
        @if (sellingPriceEuros() && estimatedCommissionEuros()) {
          <div
            class="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-5 bg-gray-2 px-4 py-3 text-xs"
          >
            <div class="flex items-center gap-2 text-gray-11">
              <mat-icon class="text-gray-10 text-[18px] w-4.5 h-4.5">calculate</mat-icon>
              <span
                >Estimation pour le prix net vendeur de
                <strong>{{ sellingPriceEuros() | number: '1.0-0' : 'fr-FR' }} €</strong> :</span
              >
            </div>
            <div class="flex items-center gap-4">
              <div class="text-gray-12">
                Honoraires agence :
                <strong class="text-primary"
                  >{{ estimatedCommissionEuros() | number: '1.0-0' : 'fr-FR' }} €</strong
                >
              </div>
              <div class="text-gray-11">
                Prix FAI estimé :
                <strong class="text-gray-12"
                  >{{
                    sellingPriceEuros()! + estimatedCommissionEuros()! | number: '1.0-0' : 'fr-FR'
                  }}
                  €</strong
                >
              </div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- SECTION 4: Gestion des clés & Organisation des visites -->
    <div class="flex flex-col gap-4">
      <div>
        <div class="flex items-center gap-2">
          <h3 class="text-base font-semibold text-gray-12">4. Gestion des clés & Organisation des visites</h3>
          @if (model().type === PropertyMandateType.SIMPLE) {
            <span
              class="rounded-full bg-amber-a3 px-2.5 py-0.5 text-[11px] font-medium text-amber-11 border border-amber-a5"
            >
              Règle mandat simple
            </span>
          }
        </div>
        <p class="mt-0.5 text-xs text-gray-11">
          @if (model().type === PropertyMandateType.SIMPLE) {
            En mandat simple avec plusieurs agences en concurrence, vous conservez vos clés pour des raisons
            de sécurité et d'assurance.
          } @else {
            Indiquez comment vous souhaitez organiser l'accès au bien lors des visites de l'agent (plusieurs
            choix possibles).
          }
        </p>
      </div>

      @if (model().type === PropertyMandateType.SIMPLE) {
        <!-- Mode exclusif mandat simple: RDV en présence uniquement -->
        <div class="flex items-start gap-4 rounded-2xl border border-amber-a6 bg-amber-a2 p-5 transition-all">
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-a3 text-amber-11 border border-amber-a5 shadow-xs"
          >
            <mat-icon class="text-[22px]! w-5.5! h-5.5! flex items-center justify-center"
              >event_available</mat-icon
            >
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-semibold text-gray-12">
                Visites sur rendez-vous uniquement en votre présence
              </span>
              <span
                class="rounded-full bg-amber-a3 px-2 py-0.5 text-[10px] font-semibold text-amber-11 border border-amber-a4"
              >
                Obligatoire en mandat simple
              </span>
            </div>
            <p class="mt-1.5 text-xs text-gray-11 leading-relaxed">
              Plusieurs agences étant mandatées en parallèle, aucun double de clé n'est confié en agence et
              aucune boîte à clés n'est autorisée. Vous gardez la maîtrise de vos clés : chaque agent doit
              obligatoirement planifier ses visites avec vous à l'avance, et vous assurez l'ouverture du
              logement.
            </p>
          </div>
        </div>
      } @else {
        <!-- Mandat Exclusif ou Semi-Exclusif: Choix entre 3 options -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          @for (item of keyManagementOptions; track item.value) {
            @let isSelected = isKeyManagementSelected(item.value);
            <button
              type="button"
              (click)="toggleKeyManagement(item.value)"
              class="flex flex-col rounded-2xl border p-4 text-left transition-all duration-200 cursor-pointer"
              [class.border-primary]="isSelected"
              [class.bg-primary-a1]="isSelected"
              [class.ring-1]="isSelected"
              [class.ring-primary]="isSelected"
              [class.border-gray-6]="!isSelected"
              [class.bg-gray-1]="!isSelected"
              [class.hover:border-gray-8]="!isSelected"
            >
              <div class="flex items-center justify-between">
                <div
                  class="flex h-9 w-9 items-center justify-center rounded-xl transition-all shadow-xs"
                  [class.bg-primary]="isSelected"
                  [class.text-white]="isSelected"
                  [class.bg-indigo-a3]="!isSelected"
                  [class.text-indigo-11]="!isSelected"
                  [class.border]="!isSelected"
                  [class.border-indigo-a5]="!isSelected"
                >
                  <mat-icon
                    class="text-[18px]! w-4.5! h-4.5! flex items-center justify-center"
                    [class.text-white!]="isSelected"
                    [class.text-indigo-11!]="!isSelected"
                    >{{ item.icon }}</mat-icon
                  >
                </div>

                <div class="flex items-center gap-2">
                  @if (item.badge) {
                    <span
                      class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      [class.bg-primary/10]="isSelected"
                      [class.text-primary]="isSelected"
                      [class.bg-gray-3]="!isSelected"
                      [class.text-gray-11]="!isSelected"
                    >
                      {{ item.badge }}
                    </span>
                  }
                  <div
                    class="flex h-5 w-5 items-center justify-center rounded-md border transition-all"
                    [class.bg-primary]="isSelected"
                    [class.border-primary]="isSelected"
                    [class.text-white]="isSelected"
                    [class.border-gray-7]="!isSelected"
                    [class.bg-surface-default]="!isSelected"
                  >
                    @if (isSelected) {
                      <mat-icon class="text-sm! w-3.5! h-3.5! flex items-center justify-center text-white!"
                        >check</mat-icon
                      >
                    }
                  </div>
                </div>
              </div>

              <div class="mt-3 flex-1">
                <div class="flex items-center justify-between">
                  <span
                    class="text-sm font-semibold"
                    [class.text-primary]="isSelected"
                    [class.text-gray-12]="!isSelected"
                  >
                    {{ item.label }}
                  </span>
                </div>
                <p class="mt-1 text-xs text-gray-11 leading-relaxed">{{ item.description }}</p>
              </div>
            </button>
          }
        </div>
      }

      <!-- Note pédagogique: Pourquoi l'absence du propriétaire favorise la vente -->
      <div
        class="flex items-start gap-3 rounded-xl border border-indigo-a4 bg-indigo-a2 p-3.5 text-xs text-gray-12"
      >
        <mat-icon class="text-indigo-11 text-[18px]! w-4.5! h-4.5! shrink-0 mt-0.5">lightbulb</mat-icon>
        <div class="flex-1 leading-relaxed">
          <strong class="text-indigo-12 font-semibold"
            >Conseil pro — Pourquoi l'absence du propriétaire favorise la vente :</strong
          >
          <span class="text-gray-11 ml-1">
            Les acquéreurs se projettent beaucoup plus facilement quand ils visitent seuls avec l'agent. Ils
            osent ouvrir les placards, mesurer les pièces et formuler franchement leurs remarques ou
            hésitations sans craindre de froisser le propriétaire. Si vous devez être présent pour ouvrir la
            porte, le conseil est de vous éclipser discrètement dans une autre pièce ou de laisser l'agent
            mener l'intégralité de la visite.
          </span>
        </div>
      </div>
    </div>

    <!-- SECTION 5: Vos priorités & sensibilités -->
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-base font-semibold text-gray-12">5. Ce qui compte le plus pour vous</h3>
        <p class="mt-0.5 text-xs text-gray-11">
          Sélectionnez les critères essentiels pour votre projet. Les agents adapteront leurs services en
          conséquence.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        @for (item of priorityOptions; track item.value) {
          @let isSelected = isPrioritySelected(item.value);
          <button
            type="button"
            (click)="togglePriority(item.value)"
            class="flex items-start gap-3.5 rounded-2xl border p-4 text-left transition-all duration-200 cursor-pointer"
            [class.border-primary]="isSelected"
            [class.bg-primary-a1]="isSelected"
            [class.ring-1]="isSelected"
            [class.ring-primary]="isSelected"
            [class.border-gray-6]="!isSelected"
            [class.bg-gray-1]="!isSelected"
            [class.hover:border-gray-8]="!isSelected"
          >
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all shadow-xs"
              [class.bg-primary]="isSelected"
              [class.text-white]="isSelected"
              [class.bg-indigo-a3]="!isSelected"
              [class.text-indigo-11]="!isSelected"
              [class.border]="!isSelected"
              [class.border-indigo-a5]="!isSelected"
            >
              <mat-icon
                class="text-[20px]! w-5! h-5! flex items-center justify-center"
                [class.text-white!]="isSelected"
                [class.text-indigo-11!]="!isSelected"
                >{{ item.icon }}</mat-icon
              >
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <span
                  class="text-sm font-semibold"
                  [class.text-primary]="isSelected"
                  [class.text-gray-12]="!isSelected"
                  >{{ item.label }}</span
                >
                @if (isSelected) {
                  <mat-icon class="text-[18px]! w-4.5! h-4.5! text-primary">check_circle</mat-icon>
                }
              </div>
              <p class="mt-1 text-xs text-gray-11 leading-relaxed">{{ item.description }}</p>
            </div>
          </button>
        }
      </div>
    </div>

    <!-- SECTION 6: Message & Instructions pour les agents -->
    <div class="flex flex-col gap-3">
      <div>
        <h3 class="text-base font-semibold text-gray-12">
          6. Message libre pour les agents candidats (Optionnel)
        </h3>
        <p class="mt-0.5 text-xs text-gray-11">
          Précisez vos contraintes ou vos attentes particulières (ex: vente urgente suite à mutation, horaires
          de visites souhaités, histoire du bien...).
        </p>
      </div>

      <!-- Real-time Personal Contact Warning Alert -->
      @if (hasContactInInstructions()) {
        <aura-alert
          [alertInput]="{
            type: auraColorType.WARN,
            title: 'Coordonnées personnelles détectées',
            message:
              'Conformément aux CGU, il est strictement interdit de mentionner des informations de contact directes (téléphone, email, réseaux sociaux...). Les échanges s\'effectuent via la plateforme sécurisée.',
          }"
        ></aura-alert>
      }

      <mat-form-field class="w-full" appearance="outline">
        <mat-label>Vos consignes pour les agents</mat-label>
        <textarea
          matInput
          rows="4"
          placeholder="Ex: Je souhaite privilégier un agent connaissant parfaitement le quartier. Visites possibles le week-end..."
          [formField]="formTree.specialInstructions"
        ></textarea>
        @if (hasContactInInstructions()) {
          <mat-hint class="text-amber-11 font-medium"
            >⚠️ Coordonnées détectées : elles seront automatiquement masquées.</mat-hint
          >
        }
        <mat-hint align="end">{{ (formTree.specialInstructions().value() || '').length }} / 500</mat-hint>
      </mat-form-field>
    </div>
  </div>
</div>
